import { useQuery } from "@tanstack/react-query";
import api from "../../services/api";

// Types for redemption data
export interface RedemptionGiftCard {
  id: string;
  brand: string;
  amount: number;
  pointCost: number;
  isActive: boolean;
}

export interface Redemption {
  id: string;
  userId: string;
  giftCardId: string;
  giftCard: RedemptionGiftCard;
  pointAmount: number;
  status: "pending" | "fulfilled" | "cancelled";
  fulfillmentCode: string | null;
  fulfilledAt: string | null;
  createdAt: string;
}

export interface RedemptionHistoryProps {
  className?: string;
}

// Brand display configuration
const BRAND_LABELS: Record<string, string> = {
  apple: "Apple",
  google: "Google Play",
  amazon: "Amazon",
};

// Status configuration with colors
const STATUS_CONFIG: Record<string, { label: string; bgColor: string; textColor: string }> = {
  pending: {
    label: "Pending",
    bgColor: "bg-yellow-500/10",
    textColor: "text-yellow-500",
  },
  fulfilled: {
    label: "Fulfilled",
    bgColor: "bg-green-500/10",
    textColor: "text-green-500",
  },
  cancelled: {
    label: "Cancelled",
    bgColor: "bg-red-500/10",
    textColor: "text-red-500",
  },
};

// Fetch redemption history from API
async function fetchRedemptions(): Promise<Redemption[]> {
  const response = await api.get<{ success: boolean; data: { redemptions: Redemption[] } }>(
    "/gift-cards/redemptions"
  );
  return response.data.data.redemptions;
}

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Individual redemption item component
interface RedemptionItemProps {
  redemption: Redemption;
}

function RedemptionItem({ redemption }: RedemptionItemProps) {
  const statusConfig = STATUS_CONFIG[redemption.status] || STATUS_CONFIG.pending;
  const brandLabel = BRAND_LABELS[redemption.giftCard.brand] || redemption.giftCard.brand;

  return (
    <div className="bg-dark-700 rounded-lg p-4 border border-dark-border hover:border-dark-600 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Left side: Gift card info */}
        <div className="flex items-center gap-4">
          {/* Gift card icon */}
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-500/20 to-primary-600/20 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-6 h-6 text-primary-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
              />
            </svg>
          </div>

          {/* Details */}
          <div>
            <p className="font-semibold text-gray-100">
              {brandLabel} ${redemption.giftCard.amount}
            </p>
            <p className="text-sm text-gray-400">
              {redemption.pointAmount.toLocaleString()} points redeemed
            </p>
            <p className="text-xs text-gray-500 mt-1">{formatDate(redemption.createdAt)}</p>
          </div>
        </div>

        {/* Right side: Status and code */}
        <div className="flex flex-col sm:items-end gap-2">
          {/* Status badge */}
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}
          >
            {statusConfig.label}
          </span>

          {/* Gift card code when fulfilled */}
          {redemption.status === "fulfilled" && redemption.fulfillmentCode && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Code:</span>
              <code className="px-2 py-1 bg-dark-600 rounded text-sm font-mono text-green-400">
                {redemption.fulfillmentCode}
              </code>
            </div>
          )}

          {/* Fulfilled date */}
          {redemption.status === "fulfilled" && redemption.fulfilledAt && (
            <p className="text-xs text-gray-500">Fulfilled on {formatDate(redemption.fulfilledAt)}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Main component
export function RedemptionHistory({ className }: RedemptionHistoryProps) {
  const { data: redemptions, isLoading, error } = useQuery({
    queryKey: ["redemptions"],
    queryFn: fetchRedemptions,
  });

  // Loading state
  if (isLoading) {
    return (
      <div className={`flex flex-col items-center justify-center py-12 ${className || ""}`}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mb-4"></div>
        <p className="text-gray-400">Loading redemption history...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`card bg-red-500/10 border-red-500/20 ${className || ""}`}>
        <div className="flex items-center gap-3 text-red-400">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="font-medium">Failed to load redemption history</p>
            <p className="text-sm text-gray-400">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!redemptions || redemptions.length === 0) {
    return (
      <div className={`card text-center py-12 ${className || ""}`}>
        <div className="w-16 h-16 bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        </div>
        <p className="text-gray-300 mb-2 font-medium">No redemptions yet</p>
        <p className="text-gray-500 text-sm">
          Your redemption history will appear here once you redeem gift cards.
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-100 mb-1">Redemption History</h2>
        <p className="text-gray-400 text-sm">
          {redemptions.length} redemption{redemptions.length !== 1 ? "s" : ""} total
        </p>
      </div>

      {/* Redemption list */}
      <div className="space-y-3">
        {redemptions.map((redemption) => (
          <RedemptionItem key={redemption.id} redemption={redemption} />
        ))}
      </div>
    </div>
  );
}

export default RedemptionHistory;
